import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { MapPin, CircleNotch } from "@phosphor-icons/react";

export function AddressAutocomplete({ label, initialValue = "", placeholder, onSelect, testid }) {
  const [q, setQ] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleChange = (text) => {
    setQ(text);
    onSelect(null, text); // mark as not-yet-resolved
    clearTimeout(timer.current);
    if (text.trim().length < 3) { setResults([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geo/search", { params: { q: text } });
        setResults(data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 450);
  };

  const pick = (r) => {
    setQ(r.display_name);
    setOpen(false);
    onSelect({ lat: r.lat, lng: r.lng, address: r.display_name });
  };

  return (
    <div className="relative" ref={boxRef}>
      {label && <label className="label">{label}</label>}
      <div className="relative">
        <input
          className="input-field pr-9"
          placeholder={placeholder}
          value={q}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          data-testid={testid}
          autoComplete="off"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          {loading ? <CircleNotch size={18} className="animate-spin" /> : <MapPin size={18} weight="duotone" />}
        </span>
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-[1300] left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-slate-100 max-h-56 overflow-auto" data-testid={`${testid}-results`}>
          {results.map((r, i) => (
            <button
              key={`${r.lat}-${r.lng}-${r.display_name}`}
              type="button"
              onClick={() => pick(r)}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-sm flex gap-2 items-start border-b border-slate-50 last:border-0"
              data-testid={`${testid}-result-${i}`}
            >
              <MapPin size={16} weight="duotone" className="mt-0.5 shrink-0 text-amber-500" />
              <span className="truncate">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
